const CSRF_COOKIE_NAME = 'shasha_csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const CSRF_STORAGE_KEY = 'shasha_csrf_token_value';
const USER_STORAGE_KEY = 'user';

function getStorage(): Storage | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage;
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) {
    return null;
  }
  const value = match.slice(name.length + 1);
  return value ? decodeURIComponent(value) : null;
}

export function persistCsrfToken(token: string | null | undefined): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  const normalized = String(token || '').trim();
  if (!normalized) {
    storage.removeItem(CSRF_STORAGE_KEY);
    return;
  }
  storage.setItem(CSRF_STORAGE_KEY, normalized);
}

export function getStoredCsrfToken(): string | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }
  const value = storage.getItem(CSRF_STORAGE_KEY);
  return value ? value.trim() || null : null;
}

export function getCsrfToken(): string | null {
  return getCookie(CSRF_COOKIE_NAME) || getStoredCsrfToken();
}

export function hasSessionHint(): boolean {
  if (getCsrfToken()) {
    return true;
  }
  if (typeof localStorage === 'undefined') {
    return false;
  }
  return Boolean(localStorage.getItem(USER_STORAGE_KEY));
}

export function withCsrfHeader(headers: any = {}): any {
  const csrf = getCsrfToken();
  if (!csrf) {
    return headers;
  }
  if (headers && typeof headers.set === 'function') {
    headers.set(CSRF_HEADER_NAME, csrf);
    return headers;
  }
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const nextHeaders = new Headers(headers);
    nextHeaders.set(CSRF_HEADER_NAME, csrf);
    return nextHeaders;
  }
  const normalizedHeaders = Array.isArray(headers)
    ? Object.fromEntries(headers)
    : headers;
  return {
    ...normalizedHeaders,
    [CSRF_HEADER_NAME]: csrf,
  };
}

export function stripAuthorizationHeader(headers: any = {}): any {
  if (headers && typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    return headers;
  }
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const nextHeaders = new Headers(headers);
    nextHeaders.delete('Authorization');
    nextHeaders.delete('authorization');
    return nextHeaders;
  }
  const normalizedHeaders = Array.isArray(headers)
    ? Object.fromEntries(headers)
    : headers;
  if (!normalizedHeaders || typeof normalizedHeaders !== 'object') {
    return normalizedHeaders;
  }
  const { Authorization, authorization, ...rest } = normalizedHeaders as Record<string, unknown>;
  void Authorization;
  void authorization;
  return rest;
}

export function clearLegacyAuthStorage(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.removeItem('token');
  localStorage.removeItem('tokens');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}
